import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useServicesList,
  useCreateService,
  useUpdateService,
  useDeleteService,
  type ServiceRow,
  type ServiceInput,
} from '@/hooks/useServices';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard } from './ClinicGeneral';

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 75, 90, 120];

const DEFAULT_FORM: ServiceInput = {
  name: '',
  description: '',
  duration_minutes: 30,
  price_cents: 0,
  currency: 'DKK',
  vat_rate_percent: 25,
  color: '#6366F1',
  is_active: true,
  is_blocker: false,
  auto_send_questionnaires: [],
  pre_visit_questionnaire_template_id: null,
};

const DEFAULT_BLOCKER_FORM: ServiceInput = {
  name: '',
  description: '',
  duration_minutes: 60,
  price_cents: 0,
  currency: 'DKK',
  vat_rate_percent: 0,
  color: '#9CA3AF',
  is_active: true,
  is_blocker: true,
  auto_send_questionnaires: [],
  pre_visit_questionnaire_template_id: null,
};

export function ClinicServices() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [includeInactive, setIncludeInactive] = useState(true);
  const {
    data: services,
    isLoading,
    error,
  } = useServicesList(activeClinicId, {
    includeInactive,
  });
  const create = useCreateService(activeClinicId);
  const update = useUpdateService(activeClinicId);
  const remove = useDeleteService(activeClinicId);

  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ServiceInput>(DEFAULT_FORM);
  const [creatingBlocker, setCreatingBlocker] = useState(false);

  // Split services into treatments and blockers
  const treatments = services?.filter((s) => !(s as any).is_blocker) ?? [];
  const blockers = services?.filter((s) => (s as any).is_blocker) ?? [];

  function openCreate() {
    setEditing(null);
    setCreatingBlocker(false);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  }

  function openCreateBlocker() {
    setEditing(null);
    setCreatingBlocker(true);
    setForm(DEFAULT_BLOCKER_FORM);
    setShowForm(true);
  }

  function openEdit(svc: ServiceRow) {
    setEditing(svc);
    setCreatingBlocker(Boolean((svc as any).is_blocker));
    // auto_send_questionnaires and pre_visit_questionnaire_template_id are not in the
    // generated DB type yet (added by FORMS-8 / FORMS-10 migrations).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const svcAny = svc as any;
    const autoSend = svcAny.auto_send_questionnaires;
    setForm({
      name: svc.name,
      description: svc.description ?? '',
      duration_minutes: svc.duration_minutes,
      price_cents: svc.price_cents,
      currency: svc.currency,
      vat_rate_percent: svc.vat_rate_percent,
      color: svc.color,
      is_active: svc.is_active,
      is_blocker: svcAny.is_blocker ?? false,
      auto_send_questionnaires: Array.isArray(autoSend) ? autoSend : [],
      pre_visit_questionnaire_template_id: svcAny.pre_visit_questionnaire_template_id ?? null,
    });
    setShowForm(true);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...form });
        toast.success(t('services.toast_updated'));
      } else {
        await create.mutateAsync(form);
        toast.success(t('services.toast_created'));
      }
      setShowForm(false);
    } catch (err) {
      console.error('services save failed', err);
      toast.error(t('services.toast_error'));
    }
  }

  async function onDelete(svc: ServiceRow) {
    if (!confirm(t('services.confirm_delete', { name: svc.name }))) return;
    try {
      await remove.mutateAsync(svc.id);
      toast.success(t('services.toast_deleted'));
    } catch (err) {
      console.error('services delete failed', err);
      toast.error(t('services.toast_error'));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) return <ErrorAlert message={t('services.load_error')} />;

  return (
    <div className="space-y-6">
      {/* Treatment Types Section */}
      <SectionCard
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        readOnly={!canEdit}
      >
        <div className="mb-4 flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t('services.show_inactive')}
          </label>
          {canEdit && (
            <Button onClick={openCreate} size="sm">
              <Plus className="mr-1 h-4 w-4" />
              {t('services.new_service')}
            </Button>
          )}
        </div>

        {!treatments || treatments.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t('services.empty')}
          </div>
        ) : (
          <ServiceTable services={treatments} canEdit={canEdit} onEdit={openEdit} onDelete={onDelete} />
        )}
      </SectionCard>

      {/* Calendar Blockers Section */}
      <SectionCard
        title={t('services.blockers_title', 'Kalender blokere')}
        subtitle={t('services.blockers_subtitle', 'Opret tidsblokke som frokost, møder, eller andre aktiviteter der skal blokkere kalenderen')}
        readOnly={!canEdit}
      >
        <div className="mb-4 flex items-center justify-end">
          {canEdit && (
            <Button onClick={openCreateBlocker} size="sm" variant="secondary">
              <Plus className="mr-1 h-4 w-4" />
              {t('services.new_blocker', 'Ny blokering')}
            </Button>
          )}
        </div>

        {!blockers || blockers.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t('services.blockers_empty', 'Ingen blokeringer oprettet endnu')}
          </div>
        ) : (
          <ServiceTable services={blockers} canEdit={canEdit} onEdit={openEdit} onDelete={onDelete} />
        )}
      </SectionCard>

      {showForm && (
        <ServiceFormModal
          clinicId={activeClinicId ?? null}
          form={form}
          onChange={setForm}
          editing={editing}
          isBlocker={creatingBlocker}
          onClose={() => setShowForm(false)}
          onSubmit={onSubmit}
          pending={create.isPending || update.isPending}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
    >
      {children}
    </th>
  );
}

function ServiceTable({
  services,
  canEdit,
  onEdit,
  onDelete,
}: {
  services: ServiceRow[];
  canEdit: boolean;
  onEdit: (svc: ServiceRow) => void;
  onDelete: (svc: ServiceRow) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <Th>{t('services.col_name')}</Th>
            <Th>{t('services.col_duration')}</Th>
            <Th>{t('services.col_price')}</Th>
            <Th>{t('services.col_status')}</Th>
            <th scope="col" className="relative px-3 py-3.5">
              <span className="sr-only">{t('services.col_actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
          {services.map((svc) => (
            <tr key={svc.id}>
              <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full ring-1 ring-inset ring-black/10"
                    style={{ backgroundColor: svc.color }}
                    aria-hidden
                  />
                  {svc.name}
                </div>
                {svc.description && (
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {svc.description}
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                {svc.duration_minutes} {t('services.minutes_short')}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                {(svc.price_cents / 100).toLocaleString('da-DK', {
                  style: 'currency',
                  currency: svc.currency,
                })}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-sm">
                {svc.is_active ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                    {t('services.status_active')}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {t('services.status_inactive')}
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
                {canEdit && (
                  <div className="inline-flex gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(svc)}
                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      aria-label={t('services.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(svc)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      aria-label={t('services.delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceFormModal({
  clinicId,
  form,
  onChange,
  editing,
  isBlocker,
  onClose,
  onSubmit,
  pending,
}: {
  clinicId: string | null;
  form: ServiceInput;
  onChange: (next: ServiceInput) => void;
  editing: ServiceRow | null;
  isBlocker: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();

  const {
    data: templateLibrary,
    isLoading: templatesLoading,
  } = useFormsTemplateList(clinicId ?? undefined);

  const allTemplates = [
    ...(templateLibrary?.platform ?? []),
    ...(templateLibrary?.clinic ?? []),
  ];

  const selectedTemplates: string[] = form.auto_send_questionnaires ?? [];

  function toggleTemplate(id: string) {
    const next = selectedTemplates.includes(id)
      ? selectedTemplates.filter((t) => t !== id)
      : [...selectedTemplates, id];
    onChange({ ...form, auto_send_questionnaires: next });
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-12 max-w-lg rounded-xl bg-white shadow-xl dark:bg-card-dark">
        <form onSubmit={onSubmit} className="space-y-4 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {editing 
              ? (isBlocker ? t('services.edit_blocker_title', 'Rediger blokering') : t('services.edit_title'))
              : (isBlocker ? t('services.new_blocker', 'Ny blokering') : t('services.new_service'))}
          </h3>

          <Input
            label={t('services.field_name')}
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('services.field_description')}
            </label>
            <textarea
              value={form.description ?? ''}
              onChange={(e) => onChange({ ...form, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('services.field_duration')}
              </label>
              <select
                value={form.duration_minutes}
                onChange={(e) => onChange({ ...form, duration_minutes: Number(e.target.value) })}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} {t('services.minutes_short')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('services.field_color')}
              </label>
              <input
                type="color"
                value={form.color ?? '#6366F1'}
                onChange={(e) => onChange({ ...form, color: e.target.value })}
                className="mt-1 h-[42px] w-full cursor-pointer rounded-md border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-700"
              />
            </div>
          </div>

          {!isBlocker && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('services.field_price', { currency: form.currency ?? 'DKK' })}
                  type="number"
                  min={0}
                  step={1}
                  value={(form.price_cents / 100).toString()}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      price_cents: Math.round(Number(e.target.value) * 100),
                    })
                  }
                />
                <Input
                  label={t('services.field_vat')}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={form.vat_rate_percent?.toString() ?? '25'}
                  onChange={(e) => onChange({ ...form, vat_rate_percent: Number(e.target.value) })}
                />
              </div>
            </>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => onChange({ ...form, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t('services.field_is_active')}
          </label>

          {!isBlocker && (
            <>
              {/* Auto-send questionnaires picker (FORMS-8) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('services.field_auto_send_questionnaires', 'Auto-send questionnaires')}
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    'services.field_auto_send_questionnaires_hint',
                    'Questionnaires to automatically send when an appointment of this type is completed.',
                  )}
                </p>

                {/* Selected chips */}
                {selectedTemplates.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {selectedTemplates.map((tid) => {
                      const tpl = allTemplates.find((x) => x.id === tid);
                      const label = tpl?.display_name_da ?? tid;
                      return (
                        <span
                          key={tid}
                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                        >
                          {label}
                          <button
                            type="button"
                            onClick={() => toggleTemplate(tid)}
                            className="ml-0.5 text-indigo-400 hover:text-indigo-600"
                            aria-label={`Remove ${label}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {templatesLoading ? (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('common.loading', 'Loading…')}
                  </div>
                ) : allTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    {t(
                      'services.field_auto_send_no_templates',
                      'No Forms templates available. Provision Clinio Forms for this clinic first.',
                    )}
                  </p>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-600">
                    {allTemplates.map((tpl) => {
                      const isSelected = selectedTemplates.includes(tpl.id);
                      return (
                        <label
                          key={tpl.id}
                          className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTemplate(tpl.id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="flex-1 text-gray-900 dark:text-white">
                            {tpl.display_name_da}
                          </span>
                          {tpl.is_platform && (
                            <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                              {t('services.template_platform_badge', 'Platform')}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pre-visit questionnaire picker (FORMS-10) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('services.field_pre_visit_questionnaire', 'Pre-visit spørgeskema')}
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    'services.field_pre_visit_questionnaire_hint',
                    'Spørgeskema der automatisk sendes til patienten umiddelbart efter bekræftelse af online booking.',
                  )}
                </p>

                {templatesLoading ? (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('common.loading', 'Loading…')}
                  </div>
                ) : (
                  <select
                    value={form.pre_visit_questionnaire_template_id ?? ''}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        pre_visit_questionnaire_template_id: e.target.value || null,
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">
                      {t('services.field_pre_visit_none', '— Intet pre-visit spørgeskema —')}
                    </option>
                    {allTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.display_name_da}
                        {tpl.is_platform ? ` (Platform)` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
